/**
 * Asset metadata manager
 * Fetches and caches universe assets from Hyperliquid info API
 */

import { debugLog, debugWarn, errorLog } from './utils/logger';

export interface AssetMetadata {
  /** Asset symbol (e.g., 'ETH', 'BTC') */
  coin: string;
  /** Numeric asset ID used by Hyperliquid (e.g., 0 for ETH) */
  universeIndex: number;
  /** Size decimal places */
  szDecimals: number;
  /** Maximum leverage */
  maxLeverage: number;
  /** Price increment/tick size */
  tickSize?: string;
}

interface UniverseAssetsResponse {
  universe: Array<{
    name: string;
    szDecimals: number;
    maxLeverage: number;
  }>;
}

/**
 * Manages asset metadata with aggressive caching for better performance
 * Meta data rarely changes, so we cache for 1 hour and persist in storage
 */
export class AssetMetadataManager {
  private cache: AssetMetadata[] | null = null;
  private cacheTimestamp: number = 0;
  private cacheTTL: number = 60 * 60 * 1000; // 1 hour - meta rarely changes
  private fetchPromise: Promise<AssetMetadata[]> | null = null;
  private infoApiUrl: string;
  private storageKey: string;

  constructor(isTestnet: boolean = false) {
    this.infoApiUrl = isTestnet
      ? 'https://api.hyperliquid-testnet.xyz/info'
      : 'https://api.hyperliquid.xyz/info';
    this.storageKey = `hyperliquid_meta_${isTestnet ? 'testnet' : 'mainnet'}`;

    // Try to load from storage on initialization
    this.loadFromStorage();

    // Pre-fetch in background if cache is empty or stale
    if (!this.cache || Date.now() - this.cacheTimestamp > this.cacheTTL) {
      this.prefetchInBackground();
    }
  }

  /**
   * Pre-fetch assets in background (non-blocking)
   */
  private prefetchInBackground(): void {
    this.getUniverseAssets().catch((error) => {
      debugWarn('[AssetMetadataManager] Background prefetch failed:', error);
    });
  }

  /**
   * Load cached data from storage (browser only)
   */
  private loadFromStorage(): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return; // Not in browser environment
    }

    try {
      const stored = window.sessionStorage.getItem(this.storageKey);
      if (stored) {
        const { assets, timestamp } = JSON.parse(stored);
        const age = Date.now() - timestamp;

        // Use stored data if less than 1 hour old
        if (age < this.cacheTTL) {
          this.cache = assets;
          this.cacheTimestamp = timestamp;
          debugLog(`[AssetMetadataManager] Loaded ${assets.length} assets from storage (age: ${Math.round(age / 1000)}s)`);
        } else {
          // Clear stale data
          window.sessionStorage.removeItem(this.storageKey);
        }
      }
    } catch (error) {
      debugWarn('[AssetMetadataManager] Failed to load from storage:', error);
    }
  }

  /**
   * Save cached data to storage (browser only)
   */
  private saveToStorage(assets: AssetMetadata[]): void {
    if (typeof window === 'undefined' || !window.sessionStorage) {
      return; // Not in browser environment
    }

    try {
      const data = {
        assets,
        timestamp: this.cacheTimestamp,
      };
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      debugLog(`[AssetMetadataManager] Saved ${assets.length} assets to storage`);
    } catch (error) {
      debugWarn('[AssetMetadataManager] Failed to save to storage:', error);
    }
  }

  /**
   * Get asset metadata by symbol
   */
  async getAssetBySymbol(symbol: string): Promise<AssetMetadata | undefined> {
    const assets = await this.getUniverseAssets();
    return assets.find((asset) => asset.coin === symbol);
  }

  /**
   * Get asset ID by symbol
   */
  async getAssetId(symbol: string): Promise<number> {
    const asset = await this.getAssetBySymbol(symbol);
    if (!asset) {
      throw new Error(
        `Asset "${symbol}" not found. Please check the symbol name.`
      );
    }
    return asset.universeIndex;
  }

  /**
   * Get symbol by asset ID (reverse lookup)
   */
  async getSymbolByAssetId(assetId: number): Promise<string | undefined> {
    const assets = await this.getUniverseAssets();
    const asset = assets.find((a) => a.universeIndex === assetId);
    return asset?.coin;
  }

  /**
   * Get all universe assets (with caching)
   */
  async getUniverseAssets(): Promise<AssetMetadata[]> {
    // Return cached data if still valid
    const now = Date.now();
    if (this.cache && now - this.cacheTimestamp < this.cacheTTL) {
      return this.cache;
    }

    // If already fetching, return the existing promise
    if (this.fetchPromise) {
      return this.fetchPromise;
    }

    // Fetch fresh data
    debugLog('[AssetMetadataManager] Fetching fresh asset metadata...');
    this.fetchPromise = this.fetchUniverseAssets();

    try {
      const assets = await this.fetchPromise;
      this.cache = assets;
      this.cacheTimestamp = now;

      // Save to storage for next session
      this.saveToStorage(assets);

      debugLog(`[AssetMetadataManager] Cached ${assets.length} assets (TTL: 1 hour)`);
      return assets;
    } finally {
      this.fetchPromise = null;
    }
  }

  /**
   * Fetch universe assets from Hyperliquid info API
   */
  private async fetchUniverseAssets(): Promise<AssetMetadata[]> {
    try {
      const response = await fetch(this.infoApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'meta' }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch universe assets: ${response.statusText}`);
      }

      const data = (await response.json()) as UniverseAssetsResponse;

      return data.universe.map((asset, index) => ({
        coin: asset.name,
        universeIndex: index,
        szDecimals: asset.szDecimals,
        maxLeverage: asset.maxLeverage,
      }));
    } catch (error) {
      errorLog('[AssetMetadataManager] Failed to fetch universe assets:', error);
      throw new Error(
        `Unable to fetch asset metadata: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Clear the cache (useful for testing or forcing refresh)
   */
  clearCache(): void {
    this.cache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Get size decimal places for a symbol
   */
  async getSizeDecimals(symbol: string): Promise<number> {
    const asset = await this.getAssetBySymbol(symbol);
    if (!asset) {
      throw new Error(`Asset "${symbol}" not found`);
    }
    return asset.szDecimals;
  }

  /**
   * Get max leverage for a symbol
   */
  async getMaxLeverage(symbol: string): Promise<number> {
    const asset = await this.getAssetBySymbol(symbol);
    if (!asset) {
      throw new Error(`Asset "${symbol}" not found`);
    }
    return asset.maxLeverage;
  }
}
